/**
 * Helpers for the HttpOnly auth cookie (see docs/auth-cookie-migration-backend-spec.md).
 *
 * A backend with cookie auth sets `__Host-f360_sid` (HttpOnly, invisible to JS) and a
 * readable `XSRF-TOKEN` on every login response. The readable cookie is how the frontend
 * knows which mode it is in:
 * - present: the session lives in the cookie, so the token must not be kept in storage;
 * - absent: an older backend, a backend with the cookie switched off, or a LAN-IP dev
 *   setup where Secure cookies are dropped. The Bearer token stays, exactly as before.
 */
export const XSRF_COOKIE_NAME = 'XSRF-TOKEN';

const SESSION_TOKEN_FIELDS = ['authToken', 'accessToken'];

export function hasCookieSession(): boolean {
  try {
    return document.cookie
      .split(';')
      .some((part) => {
        const [name, value] = part.trim().split('=');
        return name === XSRF_COOKIE_NAME && !!value;
      });
  } catch {
    return false;
  }
}

/**
 * Drops the readable half of a cookie session. The HttpOnly cookie can only be cleared by
 * the backend (logout); without `XSRF-TOKEN` the frontend treats the session as gone and
 * the next login stores whatever its response gives.
 */
export function expireCookieSession(): void {
  try {
    document.cookie = `${XSRF_COOKIE_NAME}=; Max-Age=0; Path=/; Secure; SameSite=Lax`;
  } catch {
    // Cookies blocked: nothing to expire.
  }
}

const SSO_FAILED_MESSAGE = 'SSO login failed, please try again';
const SSO_ERROR_MESSAGES = ['Unauthorized, you are not allowed to login', SSO_FAILED_MESSAGE];

/**
 * Message for the SSO redirect's `?error=`. Only the backend's fixed messages are shown
 * as given, so a crafted link cannot put its own text in front of the user.
 */
export function ssoErrorMessage(error: string): string {
  return SSO_ERROR_MESSAGES.includes(error) ? error : SSO_FAILED_MESSAGE;
}

/** Copy of a user object without the session token, when the cookie carries the session. */
export function withoutSessionToken<T>(user: T): T {
  if (!user || typeof user !== 'object' || !hasCookieSession()) {
    return user;
  }
  const copy = { ...user } as Record<string, unknown>;
  SESSION_TOKEN_FIELDS.forEach((field) => delete copy[field]);
  return copy as T;
}
